import React from "react";
import { ErrorBoundary as ReactErrorBoundary } from "react-error-boundary";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AlertTriangle, RefreshCw, Home } from "lucide-react";
import { Sentry } from "@/lib/sentry";

// Error fallback component with RTL support
const ErrorFallback: React.FC<{
  error: Error;
  resetErrorBoundary: () => void;
}> = ({ error, resetErrorBoundary }) => {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4" dir="rtl">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <AlertTriangle className="h-12 w-12 text-destructive" />
          </div>
          <CardTitle className="text-chen-900 text-xl">
            משהו השתבש
          </CardTitle>
          <CardDescription className="text-muted-foreground">
            אירעה שגיאה בלתי צפויה. אנחנו עובדים על פתרון הבעיה.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-destructive/10 border border-destructive/20 rounded-md p-3">
            <p className="text-sm text-destructive font-mono text-center">
              {error.message || "שגיאה לא ידועה"}
            </p>
          </div>

          <div className="flex flex-col gap-2">
            <Button
              onClick={resetErrorBoundary}
              className="w-full"
              variant="default"
            >
              <RefreshCw className="h-4 w-4 ml-2" />
              נסה שוב
            </Button>

            <Button
              onClick={() => window.location.href = "/"}
              className="w-full"
              variant="outline"
            >
              <Home className="h-4 w-4 ml-2" />
              חזרה לדף הבית
            </Button>
          </div>

          <p className="text-xs text-muted-foreground text-center">
            אם הבעיה ממשיכה, אנא צור קשר עם התמיכה
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

// Main Error Boundary component
interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ComponentType<{ error: Error; resetErrorBoundary: () => void }>;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

const ErrorBoundary: React.FC<ErrorBoundaryProps> = ({
  children,
  fallback: FallbackComponent = ErrorFallback,
  onError
}) => {
  const handleError = React.useCallback((error: Error, errorInfo: React.ErrorInfo) => {
    // Capture error to Sentry with React component stack
    Sentry.withScope((scope) => {
      scope.setTag("component", "ErrorBoundary");
      scope.setContext("react", {
        componentStack: errorInfo.componentStack,
      });
      scope.setContext("errorInfo", {
        // Only include safe properties from errorInfo
        componentStack: errorInfo.componentStack,
        errorBoundary: true,
        inErrorBoundary: true,
      });

      // Capture the exception
      Sentry.captureException(error);
    });

    // Call custom error handler if provided
    if (onError) {
      onError(error, errorInfo);
    }

    // Also log to console in development
    if (import.meta.env.DEV) {
      console.error("Error caught by ErrorBoundary:", error);
      console.error("Component stack:", errorInfo.componentStack);
    }
  }, [onError]);

  return (
    <ReactErrorBoundary
      FallbackComponent={FallbackComponent}
      onError={handleError}
      // Enable retry on error
      onReset={() => {
        // Clear any state if needed
        if (import.meta.env.DEV) {
          console.log("Error boundary reset");
        }
      }}
    >
      {children}
    </ReactErrorBoundary>
  );
};

// Export a hook for manually reporting errors
export const useErrorReporting = () => {
  return React.useCallback((error: Error, context?: Record<string, unknown>) => {
    Sentry.withScope((scope) => {
      scope.setTag("manual", true);
      if (context) {
        scope.setContext("manual_context", context);
      }
      Sentry.captureException(error);
    });
  }, []);
};

// Export a component for wrapping specific sections
export const SectionErrorBoundary: React.FC<{
  children: React.ReactNode;
  section?: string;
  fallback?: React.ComponentType<{ error: Error; resetErrorBoundary: () => void }>;
}> = ({ children, section, fallback }) => {
  const handleError = React.useCallback((error: Error, errorInfo: React.ErrorInfo) => {
    Sentry.withScope((scope) => {
      if (section) {
        scope.setTag("section", section);
      }
      scope.setContext("react", {
        componentStack: errorInfo.componentStack,
      });
      Sentry.captureException(error);
    });
  }, [section]);

  return (
    <ReactErrorBoundary
      FallbackComponent={fallback || ErrorFallback}
      onError={handleError}
    >
      {children}
    </ReactErrorBoundary>
  );
};

export default ErrorBoundary;